import { 
  collection, 
  addDoc, 
  updateDoc, 
  doc, 
  getDocs, 
  query, 
  where,
  serverTimestamp,
  Timestamp 
} from "firebase/firestore";
import { db } from "../config/firebase";
import { firestoreCollections } from "../utils";

// Create a new coupon
export const createCoupon = async (couponData) => {
  try {
    const couponRef = collection(db, firestoreCollections.coupons);
    const newCoupon = {
      name: couponData.name,
      description: couponData.description,
      minProjectValue: Number(couponData.minProjectValue),
      offValidation: {
        condition: couponData.offValidation.condition,
        value: Number(couponData.offValidation.value)
      },
      status: couponData.status,
      type: couponData.type,
      validity: {
        from: Timestamp.fromDate(new Date(couponData.validity.from)),
        to: Timestamp.fromDate(new Date(couponData.validity.to))
      },
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };
    const docRef = await addDoc(couponRef, newCoupon);
    return { id: docRef.id, ...newCoupon };
  } catch (error) {
    throw new Error(`Error creating coupon: ${error.message}`);
  }
};

// Get all coupons
export const getAllCoupons = async () => {
  try {
    const couponsRef = collection(db, firestoreCollections.coupons);
    const querySnapshot = await getDocs(couponsRef);
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    throw new Error(`Error fetching coupons: ${error.message}`);
  }
};

// Get active coupons
export const getActiveCoupons = async () => {
  try {
    const couponsRef = collection(db, firestoreCollections.coupons);
    const q = query(couponsRef, where("status", "==", "ACTIVE"));
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    throw new Error(`Error fetching active coupons: ${error.message}`);
  }
};

// Update coupon
export const updateCoupon = async (couponId, updateData) => {
  try {
    const couponRef = doc(db, firestoreCollections.coupons, couponId);
    const updatedData = {
      name: updateData.name,
      description: updateData.description,
      minProjectValue: Number(updateData.minProjectValue),
      offValidation: {
        condition: updateData.offValidation.condition,
        value: Number(updateData.offValidation.value)
      },
      status: updateData.status,
      type: updateData.type,
      validity: {
        from: updateData.validity.from instanceof Date ? 
          Timestamp.fromDate(updateData.validity.from) :
          Timestamp.fromDate(new Date(updateData.validity.from)),
        to: updateData.validity.to instanceof Date ?
          Timestamp.fromDate(updateData.validity.to) :
          Timestamp.fromDate(new Date(updateData.validity.to))
      },
      updatedAt: serverTimestamp()
    };
    await updateDoc(couponRef, updatedData);
    return { id: couponId, ...updatedData };
  } catch (error) {
    throw new Error(`Error updating coupon: ${error.message}`);
  }
};

// Update coupon status
export const updateCouponStatus = async (couponId, status) => {
  try {
    const couponRef = doc(db, firestoreCollections.coupons, couponId);
    await updateDoc(couponRef, {
      status,
      updatedAt: serverTimestamp()
    });
    return { id: couponId, status };
  } catch (error) {
    throw new Error(`Error updating coupon status: ${error.message}`);
  }
};

// Validate coupon
export const validateCoupon = async (couponCode, projectValue) => {
  try {
    const couponsRef = collection(db, firestoreCollections.coupons);
    const q = query(
      couponsRef, 
      where("name", "==", couponCode),
      where("status", "==", "ACTIVE")
    );
    const querySnapshot = await getDocs(q);
    
    if (querySnapshot.empty) {
      return { valid: false, message: "Invalid coupon code" };
    }

    const coupon = {
      id: querySnapshot.docs[0].id,
      ...querySnapshot.docs[0].data()
    };

    const now = new Date();
    const validFrom = coupon.validity.from.toDate();
    const validTo = coupon.validity.to.toDate();

    if (now < validFrom || now > validTo) {
      return { valid: false, message: "Coupon has expired" };
    }

    if (projectValue < coupon.minProjectValue) {
      return { 
        valid: false, 
        message: `Minimum project value should be ${coupon.minProjectValue} SAR` 
      };
    }

    return { 
      valid: true, 
      discount: {
        type: coupon.offValidation.condition,
        value: coupon.offValidation.value
      }
    };
  } catch (error) {
    throw new Error(`Error validating coupon: ${error.message}`);
  }
};